import { PossibleTypesMap } from '@apollo/client';

// Apollo works best if it's local cache looks like your data model, so it can save a server round-trip
export const typenameMapping: PossibleTypesMap = {
	ContractForGetContractWhere: [ 'CustomContract', 'SignatureContract' ],
	ContractTemplate: [ 'ContractTemplateForGetContractWhere' ],
	Signature: [
		'SignatureForGetContractWhere',
		'CustomCustomerSignatureForContact',
		'ContactSignature',
	],
	CustomerSignature: [ 'CustomerSignatureForGetContractWhere' ],
	User: [
		'CustomerForGetContractWhere',
		'SignatureUserForGetContractWhere',
		'CustomerSignatureContactCustomer',
		'SignatureUser',
		'ContactCustomerForGetQuickPayment',
		'UserForSignature',
		'BaseUserForGetContractWhere',
		'ActingUserForEvent',
		'SignatureCounterSignerUser',
		'ReducedUser',
		'UserForGetQuickPayment',
		'CustomerSignatureOrgUserUser',
	],
	ClientUser: [
		'ClientUserForGetContractWhere',
		'CustomerSignatureContactCustomerClientUser',
		'CalculatedClientUser',
		'ReducedClientUser',
	],
	Service: [ 'ServicesForGetContractWhere', 'ServicesForGetQuickPayment' ],
	Organization: [
		'OrganizationForGetContractWhere',
		'CustomerSignatureContactOrganization',
		'OrganizationWithCount',
		'OrganizationForGetQuickPayment',
		'OrganizationForActingUser',
		'ReducedOrganization',
	],
	Invoice: [ 'InvoiceForGetContractWhere' ],
	OrgUser: [
		'AdminUsersForGetContractWhere',
		'CustomerSignatureOrgUser',
		'CustomerSignatureContactOrganizationAdminUsers',
		'OrgUserForGetContractWhere',
		'CustomerSignatureContactCustomerClientUserAssignedPlanner',
		'OrgUserSignerForGetContractWhere',
		'CalculatedOrgUser',
		'OrgUserForActingUserOnEvent',
		'SignatureCounterSigner',
		'ReducedOrgUser',
		'OrgUserForGetQuickPayment',
	],
	GuestUser: [ 'GuestUserForGetContractWhere' ],
	Contact: [
		'CustomerSignatureContact',
		'ContactForGetContractWhere',
		'ContactForGetQuickPayment',
	],
	PaymentInstallment: [ 'PaymentInstallmentsForGetContractWhere' ],
	Coupon: [ 'ReducedCoupon' ],
	Resource: [ 'CustomResource' ],
	Resources: [ 'CustomResources' ],
	Sponsor: [ 'CustomSponsor' ],
	Folder: [ 'CustomFolder' ],
	Inquiry: [ 'InquiryWithToken' ],
	InviteUser: [ 'InviteUserClientUser', 'InviteUserOrgUser' ],
};

/* Object of typename -> field to act as key value for that typename:
ex. we're calling requestingUserBaseId the id for a ResponseObjectForGetContractWhere object */
export const typenamesForCustomMapping: Record<string, string[]> = {
	ResponseObjectForGetContractWhere: [ 'requestingUserBaseId', 'contract.id' ],
	ResponseObjectForGetInvoiceWhere: [ 'requestingUserBaseId', 'invoice.id' ],
	ResponseObjectForGetProposalWhere: [ 'requestingUserBaseId', 'proposal.id' ],
};

export const typenamesToSafelyIgnoreInReporting = [
	'Query',
	'Mutation',
	// These count and a [something] types, and cannot be merged/should be separate
	'AccountCapabilities',
	'ContractsForVendor',
	'ContractsForCustomer',
	'ContractTemplates',
	'Contacts',
	'Invoices',
	'Proposals',
	'ProposalTemplates',
	'Organizations',
	'PendingInvitations',
	'InquiriesForOrganization',
	'OrgDashboardStats',
	'Sources',
	'InvoicesForCustomer',
	'Documents',
	'Document',
	'ServicesForActingUser',
	'ClientDashboardStats',
	// these are truly just strings - they have a "url" property and could really be represented as strings
	'InvoiceUrl',
	'MarkupUrl',
	'UploadUrl',
	'Proposal',
	'Inquiry',
	'QuickPaymentLink',
	'SigningUrl',
	'FormattedGlobalVariables',
	// basically just a Feature Flag
	'ReducedFeatureFlag',
	// this is three numbers without IDs
	'CalculateQuickPaymentSubtotal',
	// This is fine as-is
	'QuickPaymentForGetQuickPayment',
	'AuthPayload',
	'AuthedUser',
	'ReturningMetaData',
	'Source',
	'StripeConnectAccount',
	'StripeOnboardingURL',
	'QuickPayment',
	'StripePlan',
	// Turns out these are two different returns for similar subscription data
	'StripeInvoice',
	'SubscriptionObject',
	'SubscriptionPlan',
	'SubscriptionItems',
	'StripeUser',
	'LineItem',
	'PaymentInstallmentDetails',
	'IdempotencyToken',
	'Event',
	'PaymentIntent',
	'ProposalTemplate',
	'InvoiceTemplate',
	'InvoiceTemplateLineItem',
	'InvoiceTemplates',
	'InvoiceTemplatePaymentInstallment',
	'Invitation',
	'ContractTemplateMarkupUrl',
	'RedirectUrl',
	'TestingGroup',
	'PaymentHistoryItem',
	'InvoiceForPaymentHistoryItem',
	'OrganizationForPaymentHistoryItem',
	'TransactionHistoryPayout',
	'ReturningStripeMetadata',
	'ReducedCustomer',
	'ReducedContact',
	'ReducedInvoice',
	'TransactionHistoryChargeOrRefund',
	'TransactionHistoryItem',
	'TransactionHistory',
	'InquiryForm',
	'InquiryFormField',
	'InquiryFormStyle',
	'OutstandingInvoice',
	'PaymentsOutstandingSummary',
	'PaymentsSummary',
	'ContractsSummary',
	'Plan',
	'AppSubscription',
	'VerificationToken',
	'EmailNotificationGroup',
	'StripeCoupon',
	'Discount',
	'CanvaAuthorizationUrl',
	'OAuthAccessToken',
	'OAuthToken',
	'CanvaDesignList',
	'CanvaDesign',
	'CanvaDesignUrls',
	'CanvaDesignThumbnail',
	'SourcesWithDefaultSource',
	'OrganizationSources',
	'PaymentsReceived',
	'OverduePayments',
	'UpcomingPayments',
	'ProposalsSummary',
	'ProposalsStatusSummary',
	'VendorDashboardItem',
	'ContractsOutstandingSummary',
	'BillingDetails',
	'StripeAddress',
	'CreateSetupIntentForCustomerPaymentMethodResponse',
	'CheckoutSession',
	'TransactionHistoryUrl',
	'FolderCount', // this is a count of the number of folders in a customer folder, and is not a folder
	'OfflinePaymentHistory',
	'OfflinePaymentHistoryItem',
	'InvoiceForOfflinePaymentHistory',
	'OrganizationForOfflinePaymentHistory',
	'WalletQuickPayment', // the "fake" object to represent a row in a table that can either be invoices or quick payments
	'WalletQuickPaymentInvoice',
	'PaymentHistoryWithPagination',
	'AccountSessionSecrets',
	'CalendarLinksPayload',
	'SearchResult',
	'CanvaOAuthToken',
	'CanvaDesignViewFields',
	'DatePointsComparison',
	'DatePoint',
	'InvoiceForDashboardStats',
	'FailedPayments',
	'TopCustomersBySpend',
	'TopCustomerBySpend',
	'InviteUserUser',
	'InviteUserOrganization',
	'InviteUserOrganizationService',
	'ClientActionableItemVendor',
	'ClientActionableItem',
	'DocumentsNeedingAttention',
	'WebhookDelivery',
	'WebhookEndpoint',
	'WebhookEndpointSecretResult',
	'Subscription',
	'LeadFormPresentationalData',
	'LeadFormPresentationalDataOrganization',
];
